import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui';
import toast from 'react-hot-toast';

interface LoginButtonsProps {
  franchiseId?: string | null;
}

export function LoginButtons({ franchiseId }: LoginButtonsProps) {
  const { t } = useTranslation();
  const { signInWithGoogle, signInWithApple, signInAsGuest } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);

  // Check if we're on iOS/iPadOS native (Apple Sign-In available on both iPhone and iPad)
  const platform = Capacitor.getPlatform();
  const isAppleDevice = Capacitor.isNativePlatform() && (platform === 'ios');

  const handleGoogleLogin = async () => {
    console.log('[LoginButtons] Google login clicked, platform:', platform, 'isNative:', Capacitor.isNativePlatform());
    setGoogleLoading(true);
    const loadingToast = toast.loading(t('auth.messages.connecting', 'Conectando con Google...'));
    try {
      // Guardar franchiseId en localStorage si existe
      if (franchiseId) {
        localStorage.setItem('pendingFranchiseId', franchiseId);
      }
      await signInWithGoogle();
      toast.success(t('auth.messages.loginSuccess'), { id: loadingToast });
    } catch (error: any) {
      console.error('[LoginButtons] Error en login con Google:', error);
      toast.error(error.message || t('auth.messages.loginError'), { id: loadingToast });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    console.log('[LoginButtons] Apple login clicked, platform:', platform, 'isNative:', Capacitor.isNativePlatform());
    setAppleLoading(true);
    const loadingToast = toast.loading(t('auth.messages.connectingApple', 'Conectando con Apple...'));
    try {
      // Guardar franchiseId en localStorage si existe
      if (franchiseId) {
        localStorage.setItem('pendingFranchiseId', franchiseId);
      }
      await signInWithApple();
      toast.success(t('auth.messages.loginSuccess'), { id: loadingToast });
    } catch (error: any) {
      console.error('[LoginButtons] Error en login con Apple:', error);
      // Don't show error for user cancellation
      if (error.message?.includes('cancelado') || error.message?.includes('cancelled') || error.code === 'USER_CANCELLED') {
        toast.dismiss(loadingToast);
      } else {
        toast.error(error.message || t('auth.messages.loginError'), { id: loadingToast });
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setGuestLoading(true);
    try {
      // Guardar franchiseId en localStorage si existe
      if (franchiseId) {
        localStorage.setItem('pendingFranchiseId', franchiseId);
      }
      await signInAsGuest();
      toast.success(t('auth.messages.guestSuccess'));
    } catch (error: any) {
      console.error('Error en login como invitado:', error);
      toast.error(error.message || t('auth.messages.guestError'));
    } finally {
      setGuestLoading(false);
    }
  };

  const anyLoading = googleLoading || appleLoading || guestLoading;

  return (
    <div className="flex flex-col gap-3 w-full max-w-sm">
      {/* Apple Sign-In button - show on iOS and iPadOS */}
      {isAppleDevice && (
        <Button
          onClick={handleAppleLogin}
          isLoading={appleLoading}
          disabled={anyLoading && !appleLoading}
          variant="primary"
          className="w-full bg-black hover:bg-gray-900 text-white"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="currentColor"
              d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
            />
          </svg>
          {t('auth.loginPage.continueWithApple', 'Continuar con Apple')}
        </Button>
      )}

      <Button
        onClick={handleGoogleLogin}
        isLoading={googleLoading}
        disabled={anyLoading && !googleLoading}
        variant="primary"
        className="w-full"
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        {t('auth.loginPage.continueWithGoogle')}
      </Button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-hairline"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-surface text-ink-subtle">{t('auth.or')}</span>
        </div>
      </div>

      <Button
        onClick={handleGuestLogin}
        isLoading={guestLoading}
        disabled={anyLoading && !guestLoading}
        variant="secondary"
        className="w-full"
      >
        {t('auth.loginPage.continueAsGuest')}
      </Button>
    </div>
  );
}
